import { LitElement, html, nothing, unsafeCSS } from "lit"
import sheet from "./select.tailwind.css?inline"
import { PopoverPositioning } from "../lib/popover_positioning"
import Alignment from "./select/alignment"
import { Typeahead } from "./select/typeahead"
import "./select/option"
import "./select/optgroup"

const chevronIcon = html`<svg part="icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>`

const OPEN_KEYS = [ "ArrowDown", "ArrowUp", "Enter", " " ]
const OPTION_STATE = [ "data-selected", "data-highlighted" ]
const VALUE_MISSING = "Please select an item in the list."
const UNSET_DEFAULT_VALUE = Symbol()

// invalid is the host's aria-invalid, so a field's :has([aria-invalid=true])
// sees the select.
const ariaBoolean = { fromAttribute: value => value === "true", toAttribute: ariaTrue }

/**
 * <ui-select>: a single-value select that renders shadcn base-nova's select
 * (see select.css for the shadow DOM contract and feature scope).
 *
 * Its children are the list: <ui-option> (./select/option.js), <ui-optgroup>
 * (./select/optgroup.js), and <hr> separators. The shadow root slots them into
 * its listbox, so they stay the page's elements and page CSS styles them
 * (./select/options.css). The trigger shows the selected option's text. An
 * optional <ui-selectedcontent> child shows a copy of the selected option's
 * content instead, as a native <selectedcontent> does: it is copied on each
 * selection change and slotted into the trigger. The copies are new elements
 * with the same ids and no listeners.
 *
 * Focus stays on the trigger for the whole open cycle (the APG combobox +
 * aria-activedescendant pattern). The trigger reaches the highlighted option
 * and the labels in the page through ARIA element reflection, which an id
 * can't do across the shadow boundary. Browsers without it (Chrome before 135,
 * Firefox before 136) select as normal but don't announce the highlighted
 * option or the label.
 *
 * Escape is handled here rather than left to the popover's light dismiss,
 * which closes the list without cancelling the keydown. Document-level Escape
 * handlers would then fire too and close whatever surrounds us — Ui::Modal's
 * dialog, Ui::Filter::List's panel. Cancelling the event is the contract
 * Ui::Modal honours; propagation is stopped as well for handlers that don't
 * check.
 *
 * value selects the first enabled option with that value: a list may repeat a
 * value (the time zone list repeats the common zones), and only one copy shows
 * the check. The form gets that option's value, or an empty string. Setting
 * value doesn't fire change; choosing an option fires input and change.
 */
export class UiSelect extends LitElement {
  static formAssociated = true

  static properties = {
    name: { reflect: true },
    value: { reflect: true },
    placeholder: { reflect: true },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    invalid: { attribute: "aria-invalid", reflect: true, converter: ariaBoolean },
    size: { reflect: true },
    align: { reflect: true }
  }

  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true, slotAssignment: "manual" }

  static styles = unsafeCSS(sheet)

  #internals = this.attachInternals()
  #selected = null
  #highlighted = null
  #pendingHighlight = null
  #expanded = false
  #formDisabled = false
  #defaultValue = UNSET_DEFAULT_VALUE
  #listChanged = true
  #positioning
  #alignment = new Alignment(this)
  #typeahead = new Typeahead({
    getOptions: () => this.#enabledOptions,
    getCurrent: () => this.#currentOption,
    onMatch: option => this.#typeaheadMatched(option),
    isEnabled: () => !this.#disabled
  })

  // Copies the element writes into <ui-selectedcontent> aren't list changes.
  #listObserver = new MutationObserver(records => {
    if (records.some(record => isListChange(record, this))) {
      this.#listChanged = true
      this.requestUpdate()
    }
  })

  constructor() {
    super()
    this.disabled = false
    this.required = false
    this.invalid = false
    this.addEventListener("keydown", event => this.#keydown(event))
    this.addEventListener("click", event => this.#clicked(event))
    this.addEventListener("pointerover", event => this.#pointedAt(event))
    this.addEventListener("focusout", event => this.#focusLeft(event))
    this.addEventListener("invalid", () => { this.invalid = true })
    this.addEventListener("turbo:before-morph-attribute", event => this.#keepOptionState(event))
    this.addEventListener("turbo:before-morph-element", event => this.#keepSelectedContent(event))
  }

  get form() {
    return this.#internals.form
  }

  get labels() {
    return this.#internals.labels
  }

  get validity() {
    return this.#internals.validity
  }

  checkValidity() {
    return this.#internals.checkValidity()
  }

  reportValidity() {
    return this.#internals.reportValidity()
  }

  get options() {
    return [ ...this.querySelectorAll(":scope > ui-option, :scope > ui-optgroup > ui-option") ]
  }

  get selectedOption() {
    if (this.value == null || this.value === "") return null

    return this.#enabledOptions.find(option => option.value === this.value) ?? null
  }

  get open() {
    return Boolean(this.#content?.matches(":popover-open"))
  }

  show() {
    if (!this.open) this.#content.showPopover()
  }

  hide() {
    if (this.open) this.#content.hidePopover()
  }

  // The observer misses changes while the element is out of the document.
  connectedCallback() {
    super.connectedCallback()
    if (this.#defaultValue === UNSET_DEFAULT_VALUE) this.#defaultValue = this.value
    this.#listChanged = true
    this.requestUpdate()
    this.#listObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [ "value", "disabled", "label" ]
    })
  }

  // Removing an open popover hides it without a toggle event.
  disconnectedCallback() {
    super.disconnectedCallback()
    this.#listObserver.disconnect()
    this.#typeahead.stop()
    if (this.#expanded) this.#closed()
  }

  formResetCallback() {
    this.value = this.#defaultValue
  }

  formDisabledCallback(disabled) {
    this.#formDisabled = disabled
    this.requestUpdate()
  }

  willUpdate() {
    this.#syncSelection()
  }

  render() {
    return html`
      <button
        type="button"
        part=${this.#triggerPart}
        data-slot="select-trigger"
        role="combobox"
        id="trigger"
        popovertarget="content"
        ?disabled=${this.#disabled}
        data-size=${this.size || nothing}
        ?data-placeholder=${!this.#selected && Boolean(this.placeholder)}
        aria-haspopup="listbox"
        aria-expanded=${String(this.#expanded)}
        aria-controls="listbox"
        aria-invalid=${ariaTrue(this.invalid) ?? nothing}
        aria-required=${ariaTrue(this.required) ?? nothing}
        @pointerdown=${event => this.#alignment.captureOpenMethod(event)}
      ><span part="value" data-slot="select-value"><slot name="selectedcontent">${this.#label}</slot></span>${chevronIcon}</button>
      <div
        part="content"
        data-slot="select-content"
        id="content"
        popover
        data-align=${this.align || nothing}
        @beforetoggle=${this.#beforeToggled}
        @toggle=${this.#toggled}
        @mousedown=${event => event.preventDefault()}
      >
        <div part="listbox" role="listbox" id="listbox" aria-labelledby="trigger"><slot role="none"></slot></div>
      </div>
    `
  }

  firstUpdated() {
    this.#positioning = new PopoverPositioning({
      reference: this.#trigger,
      floating: this.#content,
      namePrefix: "ui-select",
      matchReferenceWidth: true
    })
  }

  updated() {
    if (this.#listChanged) {
      this.#listSlot.assign(...this.querySelectorAll(":scope > :is(ui-option, ui-optgroup, hr)"))
      this.#listChanged = false
    }

    const selectedContent = this.#selectedContent
    if (this.#selected && selectedContent) {
      this.#selectedContentSlot.assign(selectedContent)
    } else {
      this.#selectedContentSlot.assign()
    }

    this.#labelParts()
  }

  // Runs before each render, and straight after a choice so that change
  // listeners, such as an auto-submit, see the new form value.
  #syncSelection() {
    const selected = this.selectedOption

    if (selected !== this.#selected || this.#listChanged) {
      for (const option of this.options) option.toggleAttribute("data-selected", option === selected)
      this.#selectedContent?.replaceChildren(...copyContent(selected))
      this.#selected = selected
    }

    this.#internals.setFormValue(selected?.value ?? "")
    if (this.required && !selected) {
      this.#internals.setValidity({ valueMissing: true }, VALUE_MISSING)
    } else {
      this.#internals.setValidity({})
    }
  }

  // The listbox takes the labels' name too. Without a label, or without
  // element reflection, its aria-labelledby names it after the trigger, which
  // reads out the selection.
  #labelParts() {
    const labels = [ ...this.#internals.labels ]

    if (labels.length > 0) {
      this.#trigger.ariaLabelledByElements = labels
      this.#listbox.ariaLabelledByElements = labels
    } else {
      this.#trigger.ariaLabelledByElements = null
      this.#listbox.ariaLabelledByElements = [ this.#trigger ]
    }
  }

  // beforetoggle is synchronous, unlike toggle. Closing then reopening before
  // toggle fires coalesces both into one "open" toggle, so the close has to
  // clear the highlight here for the reopen to start fresh.
  #beforeToggled(event) {
    if (event.newState === "open") {
      this.#alignment.beforeToggled(event)
    } else {
      this.#highlight(null)
    }
  }

  #toggled(event) {
    if (event.newState === "open") {
      this.#opened()
    } else {
      this.#closed()
    }
  }

  // Safari doesn't focus a button on click, so opening focuses the trigger.
  // toggle fires asynchronously after showPopover(), so a key pressed in
  // between may already have moved the highlight. Keep it.
  #opened() {
    this.#expanded = true
    this.requestUpdate()
    this.#typeahead.reset()
    this.#positioning.start()
    this.#trigger.focus()

    const overlaid = this.#alignment.apply()
    const option = this.#highlighted ?? this.#selected ?? this.#pendingOption()
    this.#pendingHighlight = null

    this.#highlight(option)
    if (!overlaid) scrollIntoList(option)
  }

  #closed() {
    this.#expanded = false
    this.requestUpdate()
    this.#highlight(null)
    this.#pendingHighlight = null
    this.#alignment.reset()
    this.#positioning.stop()
  }

  // Opening from the keyboard lands on an option so aria-activedescendant is
  // set and the next arrow moves from there. A selected option wins.
  #pendingOption() {
    const options = this.#enabledOptions

    switch (this.#pendingHighlight) {
      case "first":
        return options[0] ?? null
      case "last":
        return options.at(-1) ?? null
      default:
        return null
    }
  }

  #keydown(event) {
    if (this.#typeahead.handleKey(event)) return

    const { key } = event

    if (!this.open) {
      if (OPEN_KEYS.includes(key)) {
        event.preventDefault()
        this.#alignment.captureKeyboardOpen()
        if (key === "ArrowUp") {
          this.#pendingHighlight = "last"
        } else {
          this.#pendingHighlight = "first"
        }
        this.show()
      }
      return
    }

    switch (key) {
      case "ArrowDown":
        event.preventDefault()
        this.#moveHighlight(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.#moveHighlight(-1)
        break
      case "Home":
        event.preventDefault()
        this.#highlightInView(this.#enabledOptions[0])
        break
      case "End":
        event.preventDefault()
        this.#highlightInView(this.#enabledOptions.at(-1))
        break
      case "Enter":
      case " ":
        event.preventDefault()
        if (this.#highlighted) this.#choose(this.#highlighted)
        break
      case "Escape":
        event.preventDefault()
        event.stopPropagation()
        this.hide()
        this.#trigger.focus()
        break
      case "Tab":
        this.hide()
        break
      default:
        break
    }
  }

  #moveHighlight(delta) {
    const options = this.#enabledOptions
    if (options.length === 0) return

    const index = options.indexOf(this.#highlighted)
    let next
    if (index > -1) {
      next = (index + delta + options.length) % options.length
    } else if (delta > 0) {
      next = 0
    } else {
      next = options.length - 1
    }

    this.#highlightInView(options[next])
  }

  #highlightInView(option) {
    if (!option) return

    this.#highlight(option)
    scrollIntoList(option)
  }

  #highlight(option) {
    for (const highlighted of this.querySelectorAll("ui-option[data-highlighted]")) {
      highlighted.removeAttribute("data-highlighted")
    }

    this.#highlighted = option
    option?.setAttribute("data-highlighted", "")
    this.#trigger.ariaActiveDescendantElement = option
  }

  #clicked(event) {
    const option = event.target.closest("ui-option")
    if (!option || option.disabled) return

    this.#choose(option)
  }

  #pointedAt(event) {
    const option = event.target.closest("ui-option")
    if (!option || option.disabled) return

    this.#highlight(option)
  }

  #choose(option) {
    const changed = option.value !== this.#selected?.value

    this.value = option.value
    this.invalid = false
    this.#syncSelection()
    this.hide()

    if (changed) {
      this.dispatchEvent(new Event("input", { bubbles: true, composed: true }))
      this.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  // Typing commits a match while closed and highlights it while open.
  #typeaheadMatched(option) {
    if (this.open) {
      this.#highlightInView(option)
    } else {
      this.#choose(option)
    }
  }

  #focusLeft(event) {
    if (this.contains(event.relatedTarget)) return

    this.#typeahead.reset()
  }

  // A Turbo morph renders the server's HTML, which has none of the state the
  // element writes into its children.
  #keepOptionState(event) {
    if (event.target.localName !== "ui-option") return
    if (OPTION_STATE.includes(event.detail.attributeName)) event.preventDefault()
  }

  #keepSelectedContent(event) {
    if (event.target.localName === "ui-selectedcontent") event.preventDefault()
  }

  get #enabledOptions() {
    return this.options.filter(option => !option.disabled)
  }

  get #currentOption() {
    if (this.open) return this.#highlighted

    return this.#selected
  }

  get #disabled() {
    return this.disabled || this.#formDisabled
  }

  get #label() {
    if (this.#selected) return this.#selected.label

    return this.placeholder ?? ""
  }

  // ::part(trigger open) styles the open trigger from the page.
  get #triggerPart() {
    if (this.#expanded) return "trigger open"

    return "trigger"
  }

  get #selectedContent() {
    return this.querySelector(":scope > ui-selectedcontent")
  }

  get #trigger() {
    return this.shadowRoot.getElementById("trigger")
  }

  get #content() {
    return this.shadowRoot?.getElementById("content")
  }

  get #listbox() {
    return this.shadowRoot.getElementById("listbox")
  }

  get #listSlot() {
    return this.shadowRoot.querySelector("slot:not([name])")
  }

  get #selectedContentSlot() {
    return this.shadowRoot.querySelector("slot[name='selectedcontent']")
  }
}

function isListChange(record, select) {
  if (record.type === "attributes" && record.target === select) return false

  let element = record.target
  if (element.nodeType !== Node.ELEMENT_NODE) element = element.parentElement
  return !element?.closest("ui-selectedcontent")
}

function copyContent(option) {
  if (!option) return []

  return [ ...option.childNodes ].map(node => node.cloneNode(true))
}

// The content is the scroll container; block: "nearest" honours its
// scroll-padding and leaves the page alone when the option is already visible.
function scrollIntoList(option) {
  option?.scrollIntoView({ block: "nearest", inline: "nearest" })
}

function ariaTrue(condition) {
  if (condition) return "true"

  return null
}

customElements.define("ui-select", UiSelect)
